// Live credit state, docked beside the thread — what the hold is doing, while it does it.
//
// ★ IT EXISTS BECAUSE THE INTERESTING NUMBER IS THE ONE THAT DISAPPEARS. A wallet balance tells you
//   what is left; the RESERVE appears when a turn starts and is gone a second after it ends,
//   so the only way to see the gate working is to watch it. That is also why this polls rather than
//   riding the socket: the charge and the release arrive through the broker, which the socket knows
//   nothing about, so a socket-only panel would show the hold appear and never see it settle.
//
// ⚠ A DEVELOPMENT SURFACE. It reports the tenant's whole wallet, not the caller's own spend, because
//   a hold placed by anyone moves the same reserve. /usage is the per-user view.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { billingDebugApi } from "../../../services/api";
import { inZone } from "../messageTime.mjs";
import styles from "../sqAi.module.css";

// Fast enough to catch a hold inside a turn, slow enough not to hammer MSSQL from a dev panel.
const POLL_MS = 1500;

const n = (v) => (typeof v === "number" ? v.toLocaleString() : v);
const shortTurn = (t) => String(t || "").slice(0, 8);
// The clock in the page's zone (the reader's, as every other time on the page), not the browser's.
const clock = (iso, zone) => {
  if (!iso) return "";
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? "" : inZone(ms, zone || "UTC").format("HH:mm:ss");
};

// The movement types, shortened to what fits and coloured by what they mean: money going out,
// money set aside, money coming back, money not charged.
const TYPE_STYLE = {
  RESERVATION: styles.bdHold,
  RESERVATION_RELEASE: styles.bdBack,
  CONSUMPTION: styles.bdSpend,
  OVERAGE: styles.bdOver,
  INTERNAL_ERROR: styles.bdFree,
  CANCELLED: styles.bdFree,
  MONTHLY_GRANT: styles.bdBack,
  ANNUAL_GRANT: styles.bdBack,
  ADJUSTMENT: styles.bdBack,
};
const TYPE_LABEL = {
  RESERVATION: "hold",
  RESERVATION_RELEASE: "release",
  CONSUMPTION: "charge",
  OVERAGE: "overage",
  INTERNAL_ERROR: "not charged",
  CANCELLED: "cancelled",
  MONTHLY_GRANT: "grant",
  ANNUAL_GRANT: "annual grant",
  ADJUSTMENT: "adjustment",
};

export default function BillingDebugPanel({ env, tenantSchema, zone }) {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  // Reads overlap when one outlasts POLL_MS, and a tenant switch leaves the last tenant's read in flight: a
  // response older than the newest one shown, or sent before the switch, is dropped rather than painted.
  const sentRef = useRef(0);
  const shownRef = useRef(0);
  // ONE READ AT A TIME: a tick while a read is out is skipped. Each read holds a pooled database connection on the server, so a read
  // that stalls — the wallet behind a lock, a slow registry — was joined by another every tick, and they all landed at once when it
  // cleared (B21: ~45 in a few seconds). Holds the out read's number, so a tenant switch can let its first read go.
  const inFlightRef = useRef(0);

  const poll = useCallback(async () => {
    if (!env || !tenantSchema || inFlightRef.current) return;
    const seq = ++sentRef.current;
    inFlightRef.current = seq;
    try {
      const res = await billingDebugApi.get(env, tenantSchema);
      if (seq <= shownRef.current) return;
      shownRef.current = seq;
      setData(res.data);
      setError(null);
    } catch (err) {
      if (seq <= shownRef.current) return;
      shownRef.current = seq;
      const said = err && err.response && err.response.data && err.response.data.error;
      setError(said || "could not read billing state");
    } finally {
      if (inFlightRef.current === seq) inFlightRef.current = 0;
    }
  }, [env, tenantSchema]);

  // Declared before the polling effect so it runs first: the switch clears the last tenant's figures and
  // drops its reads, then the new tenant's first read is sent.
  useEffect(() => {
    shownRef.current = sentRef.current;
    inFlightRef.current = 0;
    setData(null);
    setError(null);
  }, [env, tenantSchema]);

  useEffect(() => {
    if (!open || !env || !tenantSchema) return undefined;
    poll();
    timerRef.current = setInterval(poll, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [open, env, tenantSchema, poll]);

  if (!env || !tenantSchema) return null;

  if (!open) {
    return (
      <button type="button" className={styles.bdTab} onClick={() => setOpen(true)} title="Show credit state">
        <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2} />
        <span>Credit</span>
      </button>
    );
  }

  const w = data && data.wallet;
  const reserved = w ? w.reserved : 0;

  return (
    <aside className={styles.bdPanel} aria-label="Live credit state">
      <div className={styles.bdHead}>
        <span className={styles.bdTitle}>Credit · {tenantSchema}</span>
        <button type="button" className={styles.bdClose} onClick={() => setOpen(false)} aria-label="Hide credit state">
          <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </div>

      {error && <p className={styles.bdNote}>{error}</p>}
      {!error && data && !data.enrolled && <p className={styles.bdNote}>No live wallet — this tenant is unmetered.</p>}

      {!error && w && (
        <>
          {/* Reserved is the live one: it is the only figure that moves inside a turn, so it is
              given its own row and lights up while anything is held. */}
          <div className={`${styles.bdReserve} ${reserved > 0 ? styles.bdReserveOn : ""}`}>
            <span className={styles.bdReserveLabel}>{reserved > 0 ? "held right now" : "nothing held"}</span>
            <span className={styles.bdReserveValue}>{n(reserved)}</span>
          </div>

          <dl className={styles.bdStats}>
            <div><dt>available</dt><dd>{n(w.available)}</dd></div>
            <div><dt>balance</dt><dd>{n(w.balance)}</dd></div>
          </dl>

          <h4 className={styles.bdSub}>Holds</h4>
          {data.holds.length === 0 && <p className={styles.bdNote}>None yet.</p>}
          {data.holds.map((h) => (
            <div key={h.turnId} className={styles.bdRow}>
              <span className={styles.bdMono}>{shortTurn(h.turnId)}</span>
              <span className={h.state === "HELD" ? styles.bdHold : h.state === "SETTLED" ? styles.bdBack : styles.bdOver}>
                {h.state === "HELD" ? "HELD" : h.state === "SETTLED" ? "settled" : h.state.replace("RELEASED_", "").toLowerCase()}
              </span>
              <span className={styles.bdRight}>{n(h.credits)}</span>
            </div>
          ))}

          <h4 className={styles.bdSub}>Movements</h4>
          {data.ledger.map((r) => (
            <div key={r.transactionId} className={styles.bdRow}>
              <span className={styles.bdMono}>{clock(r.at, zone)}</span>
              <span className={TYPE_STYLE[r.type] || ""}>{TYPE_LABEL[r.type] || r.type.toLowerCase()}</span>
              <span className={styles.bdRight}>{n(r.credits)}</span>
            </div>
          ))}
        </>
      )}
    </aside>
  );
}
